import {useReports} from '@sail/observability';
import {useUpdatedRef} from '@sail/react';
import {useCallback, useContext, useMemo} from 'react';
import HrefContext, {defaultSetHref} from 'src/internal/contexts/HrefContext';

import type {Href, HrefHook} from 'src/types';

/**
 * @see https://sail.stripe.me/apis/navigation/useHref
 */
export default function useHref(): HrefHook {
  const [href, setHref] = useContext(HrefContext);
  const {error} = useReports();
  const hrefRef = useUpdatedRef(href);
  const setHrefRef = useUpdatedRef(setHref);

  if (setHref === defaultSetHref) {
    error(
      'Setters for `href` are not configuring, navigating using `@sail/navigation` will not work.',
    );
  }

  const setDeduplicatedHref = useCallback(
    (newHref: Href | ((oldHref: Href) => Href), replace?: boolean) => {
      const resolvedHref =
        typeof newHref === 'function' ? newHref(hrefRef.current) : newHref;

      if (hrefRef.current !== resolvedHref) {
        setHrefRef.current(resolvedHref, !!replace);
      }
    },
    [setHrefRef, hrefRef],
  );

  return useMemo(
    () => [href, setDeduplicatedHref],
    [href, setDeduplicatedHref],
  );
}
